import { ElementType, KeyboardEvent } from 'react'

import { Box, Grid, Icon, useTheme } from '@chakra-ui/react'

import { MENU_ITEM_STYLES } from './constants'

const getIconColorProps = (useStroke: boolean, color: string) =>
  useStroke ? { strokeColor: color } : { color }

export interface MenuItemIProps {
  icon: ElementType
  title: string
  onClick?: () => void
  href?: string
  dataTestId?: string
  useStrokeColor?: boolean
}

export const MenuItem = ({
  icon,
  title,
  onClick,
  href,
  dataTestId,
  useStrokeColor = false,
}: MenuItemIProps) => {
  const theme = useTheme()

  const handleKeyDown = (event: KeyboardEvent) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      onClick()
    }
  }

  const content = (
    <>
      <Icon
        as={icon}
        boxSize={MENU_ITEM_STYLES.ICON_SIZE}
        {...getIconColorProps(useStrokeColor, theme.colors.sidebar.darkText)}
      />
      <Box
        color={theme.colors.sidebar.darkText}
        fontSize={MENU_ITEM_STYLES.TEXT_FONT_SIZE}
        fontWeight='normal'
        lineHeight='shorter'
        textAlign='left'
      >
        {title}
      </Box>
    </>
  )

  const commonProps = {
    _hover: {
      bgColor: 'gray.50',
    },
    alignItems: 'center',
    cursor: 'pointer',
    'data-testid': dataTestId,
    gap: MENU_ITEM_STYLES.GAP,
    p: MENU_ITEM_STYLES.PADDING,
    rounded: MENU_ITEM_STYLES.BORDER_RADIUS,
    templateColumns: `${MENU_ITEM_STYLES.ICON_SIZE} 1fr`,
    transition: 'ease-in-out',
    transitionDuration: MENU_ITEM_STYLES.TRANSITION_DURATION,
    transitionProperty: 'all',
  }

  if (href) {
    return (
      <Grid
        {...commonProps}
        as='a'
        href={href}
        rel='noopener noreferrer'
        target='_blank'
      >
        {content}
      </Grid>
    )
  }

  return (
    <Grid
      {...commonProps}
      as='button'
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      type='button'
      w='100%'
    >
      {content}
    </Grid>
  )
}
